'use client'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/providers/Auth'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

type FormData = {
  email: string
  password: string
  passwordConfirm: string
}

export const CreateAccountForm: React.FC = () => {
  const searchParams = useSearchParams()
  const allParams = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const { login } = useAuth()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | string>(null)

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<FormData>()

  const password = useRef({})
  password.current = watch('password', '')

  const onSubmit = useCallback(
    async (data: FormData) => {
      const response = await fetch(`/api/users`, {
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (!response.ok) {
        const message = response.statusText || 'There was an error creating the account.'
        setError(message)
        return
      }

      const redirect = searchParams.get('redirect')

      const timer = setTimeout(() => {
        setLoading(true)
      }, 1000)

      try {
        await login(data)
        clearTimeout(timer)
        if (redirect) router.push(redirect)
        else router.push(`/account?success=${encodeURIComponent('Account created successfully')}`)
      } catch (_) {
        clearTimeout(timer)
        setError('There was an error with the credentials provided. Please try again.')
      }
    },
    [login, router, searchParams],
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Message className="mb-6" error={error} />

      <div className="space-y-5">
        <FormItem>
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            E-mailadres
          </Label>
          <Input
            id="email"
            {...register('email', { required: 'E-mailadres is verplicht.' })}
            type="email"
            autoComplete="email"
            placeholder="naam@voorbeeld.nl"
            className="h-11 rounded-lg border-border/60 bg-background/50 transition-colors focus:border-primary/50 focus:ring-primary/20"
            autoFocus
          />
          {errors.email && <FormError message={errors.email.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="password" className="text-sm font-medium text-foreground">
            Wachtwoord
          </Label>
          <Input
            id="password"
            {...register('password', { required: 'Wachtwoord is verplicht.' })}
            type="password"
            autoComplete="new-password"
            placeholder="Kies een nieuw wachtwoord"
            className="h-11 rounded-lg border-border/60 bg-background/50 placeholder:text-muted-foreground/45 transition-colors focus:border-primary/50 focus:ring-primary/20"
          />
          {errors.password && <FormError message={errors.password.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="passwordConfirm" className="text-sm font-medium text-foreground">
            Bevestig wachtwoord
          </Label>
          <Input
            id="passwordConfirm"
            {...register('passwordConfirm', {
              required: 'Bevestig je wachtwoord.',
              validate: (value) => value === password.current || 'Wachtwoorden komen niet overeen',
            })}
            type="password"
            autoComplete="new-password"
            placeholder="Voer hetzelfde wachtwoord opnieuw in"
            className="h-11 rounded-lg border-border/60 bg-background/50 placeholder:text-muted-foreground/45 transition-colors focus:border-primary/50 focus:ring-primary/20"
          />
          {errors.passwordConfirm && <FormError message={errors.passwordConfirm.message} />}
        </FormItem>
      </div>

      <div className="mt-8 space-y-3">
        <Button
          className="w-full h-11 rounded-lg font-medium"
          disabled={loading}
          type="submit"
          variant="default"
        >
          {loading ? 'Account aanmaken...' : 'Account aanmaken'}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card/50 px-3 text-muted-foreground">of</span>
          </div>
        </div>

        <Button asChild variant="outline" size="lg" className="w-full h-11 rounded-lg font-medium">
          <Link href={`/inloggen${allParams}`}>Inloggen</Link>
        </Button>
      </div>
    </form>
  )
}
